import React, { useEffect, useMemo, useState } from 'react';
import { Search, Filter, Download, Eye, User, Clock, MapPin } from 'lucide-react';
import AdminPageShell from "../components/AdminPageShell";
import API from '../../../util/API';

const cx = (...arr) => arr.filter(Boolean).join(' ');

const Card = ({ children, className }) => (
  <div className={cx('bg-white border border-slate-200 rounded-2xl', className)}>{children}</div>
);

const FieldBase =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30';

const SelectBase =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#D2042D]/30';

const PrimaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      'inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold text-white bg-slate-900 hover:bg-[#D2042D] transition-colors',
      className
    )}
  />
);

const SecondaryButton = ({ className, ...props }) => (
  <button
    {...props}
    className={cx(
      'inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-sm font-semibold border border-slate-200 bg-white text-slate-900 hover:bg-slate-50 transition-colors',
      className
    )}
  />
);

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  // eslint-disable-next-line no-restricted-globals
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString();
};

const getStatusFromCode = (code) => {
  const c = Number(code || 0);
  if (c >= 200 && c <= 299) return 'success';
  if (c >= 300 && c <= 399) return 'warning';
  if (c >= 400) return 'failed';
  return 'success';
};

const buildCsv = (rows) => {
  const esc = (v) => {
    const s = String(v ?? '');
    return `"${s.replace(/"/g, '""')}"`;
  };
  const header = [
    'timestamp',
    'actorType',
    'actorEmail',
    'actorUsername',
    'method',
    'path',
    'statusCode',
    'durationMs',
    'ip',
    'userAgent',
  ];
  const lines = [header.map(esc).join(',')];
  for (const r of rows) {
    lines.push(
      [
        r.createdAt,
        r.actorType,
        r.actorEmail,
        r.actorUsername,
        r.method,
        r.path,
        r.statusCode,
        r.durationMs,
        r.ip,
        r.userAgent,
      ]
        .map(esc)
        .join(',')
    );
  }
  return lines.join('\n');
};

const ActivityLogs = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [logs, setLogs] = useState([]);

  const [search, setSearch] = useState('');
  const [selectedActorType, setSelectedActorType] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [selectedLog, setSelectedLog] = useState(null);

  const actorTypes = useMemo(
    () => ['admin', 'user', 'vendor', 'guest'],
    []
  );
  const methods = useMemo(() => ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'], []);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: 1,
        limit: 200,
        search: search || undefined,
        actorType: selectedActorType || undefined,
        method: selectedMethod || undefined,
        status: selectedStatus || undefined,
        from: dateRange.start ? new Date(dateRange.start).toISOString() : undefined,
        to: dateRange.end ? new Date(dateRange.end + 'T23:59:59.999Z').toISOString() : undefined,
      };
      const res = await API.get('/log', { params });
      setLogs(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredLogs = useMemo(() => {
    // We already filter server-side, but keep a tiny client-side safety net.
    const s = String(search || '').trim().toLowerCase();
    if (!s) return logs;
    return logs.filter((l) =>
      String(l.actorEmail || '').toLowerCase().includes(s) ||
      String(l.actorUsername || '').toLowerCase().includes(s) ||
      String(l.path || '').toLowerCase().includes(s) ||
      String(l.message || '').toLowerCase().includes(s)
    );
  }, [logs, search]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'border-emerald-200 bg-emerald-50 text-emerald-800';
      case 'failed': return 'border-rose-200 bg-rose-50 text-rose-800';
      case 'warning': return 'border-amber-200 bg-amber-50 text-amber-800';
      default: return 'border-slate-200 bg-slate-50 text-slate-800';
    }
  };

  return (
    <AdminPageShell
      title="Activity Logs"
      description="Audit admin/user/vendor activity across the API (Unified logs)."
      actions={
        <PrimaryButton
          onClick={() => {
            const csv = buildCsv(filteredLogs);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `activity-logs-${new Date().toISOString().slice(0, 10)}.csv`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          disabled={loading || filteredLogs.length === 0}
        >
          <Download size={16} /> Export
        </PrimaryButton>
      }
    >
      {/* Filters */}
      <Card className="p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Search logs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={cx(FieldBase, 'pl-11')}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Actor Type</label>
            <select
              value={selectedActorType}
              onChange={(e) => setSelectedActorType(e.target.value)}
              className={SelectBase}
            >
              <option value="">All</option>
              {actorTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Method</label>
            <select
              value={selectedMethod}
              onChange={(e) => setSelectedMethod(e.target.value)}
              className={SelectBase}
            >
              <option value="">All</option>
              {methods.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className={SelectBase}
            >
              <option value="">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="warning">Warning</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">Start Date</label>
            <input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              className={FieldBase}
            />
          </div>
          <div>
            <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">End Date</label>
            <input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              className={FieldBase}
            />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <PrimaryButton>
            <Filter size={16} /> Apply Filters
          </PrimaryButton>
          <SecondaryButton
            onClick={() => {
              setSearch('');
              setSelectedActorType('');
              setSelectedMethod('');
              setSelectedStatus('');
              setDateRange({ start: '', end: '' });
              setError('');
            }}
          >
            Clear
          </SecondaryButton>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <SecondaryButton onClick={load} className="px-4 py-2">
            Refresh
          </SecondaryButton>
          {loading ? <div className="text-sm text-slate-500">Loading…</div> : null}
          {error ? <div className="text-sm text-rose-700">{error}</div> : null}
        </div>
      </Card>

      {/* Activity Logs Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Admin</th>
              <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Action</th>
              <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Details</th>
              <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">IP Address</th>
              <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Location</th>
              <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Timestamp</th>
              <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-black tracking-wide text-slate-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100">
            {filteredLogs.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-10 text-slate-500">No activity logs found.</td>
              </tr>
            ) : (
              filteredLogs.map((log) => (
                <tr key={log._id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-slate-50 border border-slate-200 rounded-full flex items-center justify-center">
                        <User className="text-slate-700" size={14} />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-semibold text-slate-900">{log.actorUsername || '—'}</p>
                        <p className="text-xs text-slate-500">{log.actorEmail || log.actorType || 'guest'}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm font-semibold text-slate-900">{log.method} {log.path}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-slate-900 max-w-xs truncate">Status {log.statusCode} • {log.durationMs}ms</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-slate-600 font-mono">{log.ip || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <MapPin className="text-slate-400" size={12} />
                      <span className="text-sm text-slate-600">—</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Clock className="text-slate-400" size={12} />
                      <span className="text-sm text-slate-600">{formatDateTime(log.createdAt)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const status = getStatusFromCode(log.statusCode);
                      return (
                        <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold capitalize ${getStatusColor(status)}`}>
                          {status}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelectedLog(log)}
                      className="h-9 w-9 inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-[#D2042D] transition-colors"
                      title="View Details"
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          </table>
        </div>
      </Card>

      {/* Log Details Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-base md:text-lg font-black tracking-tight text-slate-900">Activity Log Details</h2>
              <SecondaryButton onClick={() => setSelectedLog(null)} className="px-3 py-1.5">
                Close
              </SecondaryButton>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500">Actor Type</label>
                  <p className="text-sm text-slate-900">{selectedLog.actorType || 'guest'}</p>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500">Actor Email</label>
                  <p className="text-sm text-slate-900">{selectedLog.actorEmail || '—'}</p>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500">Request</label>
                  <p className="text-sm text-slate-900">{selectedLog.method} {selectedLog.path}</p>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500">Status</label>
                  {(() => {
                    const status = getStatusFromCode(selectedLog.statusCode);
                    return (
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-bold capitalize ${getStatusColor(status)}`}>
                        {status} ({selectedLog.statusCode})
                      </span>
                    );
                  })()}
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500">IP Address</label>
                  <p className="text-sm text-slate-900 font-mono">{selectedLog.ip || '—'}</p>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500">Location</label>
                  <p className="text-sm text-slate-900">—</p>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500">Timestamp</label>
                  <p className="text-sm text-slate-900">{formatDateTime(selectedLog.createdAt)}</p>
                </div>
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500">Duration</label>
                  <p className="text-sm text-slate-900">{selectedLog.durationMs}ms</p>
                </div>
              </div>
              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500">Details</label>
                <p className="text-sm text-slate-900">
                  {selectedLog.message || '—'}
                </p>
              </div>
              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500">User Agent</label>
                <p className="text-sm text-slate-900 font-mono">{selectedLog.userAgent || '—'}</p>
              </div>
              {selectedLog?.meta?.error?.message ? (
                <div>
                  <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500">Error</label>
                  <p className="text-sm text-rose-800">{selectedLog.meta.error.message}</p>
                </div>
              ) : null}
              <div>
                <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500">Query</label>
                <pre className="text-xs text-slate-900 bg-slate-50 border border-slate-200 rounded-2xl p-3 overflow-auto">
                  {JSON.stringify(selectedLog.query || {}, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminPageShell>
  );
};

export default ActivityLogs; 
